/**
 * Custom Cart Drawer Component (Section Version)
 * Handles dynamic offer thresholds, progress bar, cart operations,
 * and special order instructions synced to Shopify cart note.
 */
class CustomCartDrawer extends HTMLElement {
    constructor() {
        super();
        this.classes = {
            active: 'active',
            loading: 'is-loading'
        };
        this.removedVariantIds = new Set(); // Track user-removed gifts
        this.lastUnlockedCount = 0;         // Track milestones for confetti
        this.firstLoad = true;              // Suppress confetti on initial load
        this.qtyBusy = false;               // Prevent double-fire on qty buttons
        this.couponListenerBound = false;   // Prevent duplicate coupon-click listeners
    }


    connectedCallback() {
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.handleGlobalSubmit = this.handleGlobalSubmit.bind(this);
        this.changeQty = this.changeQty.bind(this);
        this.handleCartUpdateGlobal = this.handleCartUpdateGlobal.bind(this);

        document.addEventListener('custom-cart:open', this.open);
        document.addEventListener('cart:item-added', () => { this.fetchCart(); this.open(); });
        document.addEventListener('cart:updated', () => this.fetchCart());
        document.addEventListener('cart:update', this.handleCartUpdateGlobal);
        document.addEventListener('submit', this.handleGlobalSubmit);

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init());
        } else {
            this.init();
        }
    }

    disconnectedCallback() {
        document.removeEventListener('custom-cart:open', this.open);
        document.removeEventListener('cart:update', this.handleCartUpdateGlobal);
        document.removeEventListener('submit', this.handleGlobalSubmit);
        this.removeEventListener('click', this.handleClick);
    }

    handleCartUpdateGlobal(e) {
        if (e.detail && e.detail.data && e.detail.data.source === 'custom-cart-drawer') return;
        this.fetchCart();
    }


    init() {
        this.drawer          = this.querySelector('.ccd-drawer');
        this.overlay         = this.querySelector('.ccd-overlay');
        this.progressBar     = this.querySelector('.ccd-progress-fill');
        this.progressText    = this.querySelector('.ccd-progress-text');
        this.itemsContainer  = this.querySelector('.ccd-items-wrapper');
        this.milestoneLayer  = this.querySelector('.ccd-milestones-layer');


        this.offers = this.getOffers();
        console.log('CCD: Offers loaded:', this.offers);


        if (this.offers.length > 0) {
            this.offers.sort((a, b) => a.threshold - b.threshold);
            this.renderMilestones();
        } else {
            setTimeout(() => {
                this.offers = this.getOffers();
                if (this.offers.length > 0) {
                    this.offers.sort((a, b) => a.threshold - b.threshold);
                    this.renderMilestones();
                    if (this.lastCart) this.updateProgress(this.lastCart);
                }
            }, 500);
        }


        this.removeEventListener('click', this.handleClick);
        this.addEventListener('click', this.handleClick);


        this.fetchCart();
        this.initCoupons();
        this.initSpecialInstructions();
        this.bindFooterEvents();
    }


    // ─────────────────────────────────────────────
    //  SPECIAL INSTRUCTIONS
    // ─────────────────────────────────────────────


    initSpecialInstructions() {
        const toggle      = this.querySelector('#ccd-instructions-toggle');
        const body        = this.querySelector('#ccd-instructions-body');
        const textarea    = this.querySelector('#ccd-note-input');
        const saveBtn     = this.querySelector('#ccd-save-note');
        const savedMsg    = this.querySelector('#ccd-note-saved');
        const errorMsg    = this.querySelector('#ccd-note-error');
        const hiddenInput = this.querySelector('#ccd-note-hidden');


        if (!toggle || !textarea) return;


        // ── Restore saved note ──────────────────────
        // Priority: Shopify cart note (via hidden field set in Liquid) → sessionStorage fallback
        const liquidNote    = hiddenInput ? hiddenInput.value.trim() : '';
        const sessionNote   = sessionStorage.getItem('ccd_cart_note') || '';
        const restoredNote  = liquidNote || sessionNote;


        if (restoredNote) {
            textarea.value = restoredNote;
            if (hiddenInput) hiddenInput.value = restoredNote;
            // Auto-open the panel if a note already exists
            this._openInstructions(toggle, body);
        }


        // ── Toggle open / close ─────────────────────
        toggle.addEventListener('click', () => {
            const isOpen = body.classList.contains('open');
            if (isOpen) {
                this._closeInstructions(toggle, body);
            } else {
                this._openInstructions(toggle, body);
            }
        });


        // ── Live sync to hidden checkout field ──────
        textarea.addEventListener('input', () => {
            if (hiddenInput) hiddenInput.value = textarea.value;
            // Reset feedback messages on new input
            if (savedMsg) savedMsg.style.display = 'none';
            if (errorMsg) errorMsg.style.display = 'none';
        });


        // ── Save button → POST to /cart/update.js ──
        if (saveBtn) {
            saveBtn.addEventListener('click', async () => {
                const note = textarea.value.trim();
                saveBtn.disabled = true;
                saveBtn.textContent = 'Saving...';
                if (savedMsg) savedMsg.style.display = 'none';
                if (errorMsg) errorMsg.style.display = 'none';


                try {
                    const res = await fetch('/cart/update.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ note: note })
                    });


                    if (!res.ok) throw new Error('Cart update failed');


                    // Persist locally so it survives re-renders
                    sessionStorage.setItem('ccd_cart_note', note);
                    if (hiddenInput) hiddenInput.value = note;


                    if (savedMsg) {
                        savedMsg.style.display = 'inline';
                        setTimeout(() => { savedMsg.style.display = 'none'; }, 3000);
                    }
                    console.log('CCD: Cart note saved →', note);


                } catch (e) {
                    console.error('CCD: Note save failed', e);
                    if (errorMsg) {
                        errorMsg.style.display = 'inline';
                        setTimeout(() => { errorMsg.style.display = 'none'; }, 4000);
                    }
                } finally {
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                }
            });
        }
    }


    _openInstructions(toggle, body) {
        body.classList.add('open');
        toggle.classList.add('open');
    }


    _closeInstructions(toggle, body) {
        body.classList.remove('open');
        toggle.classList.remove('open');
    }


    // ─────────────────────────────────────────────
    //  FOOTER / CHECKOUT
    // ─────────────────────────────────────────────


    bindFooterEvents() {
        const checkoutForm = this.querySelector('#ccd-cart-form');
        if (checkoutForm) {
            // If a discount code is active, redirect to checkout with it applied
            checkoutForm.addEventListener('submit', (e) => {
                const activeCode = sessionStorage.getItem('ccd_discount_code');
                if (activeCode) {
                    e.preventDefault();
                    // Sync note one last time before navigating
                    const textarea    = this.querySelector('#ccd-note-input');
                    const hiddenInput = this.querySelector('#ccd-note-hidden');
                    if (textarea && hiddenInput) hiddenInput.value = textarea.value;


                    // Build URL with discount applied
                    const note = hiddenInput ? hiddenInput.value : '';
                    let url = `/checkout?discount=${encodeURIComponent(activeCode)}`;
                    if (note) url += `&note=${encodeURIComponent(note)}`;
                    window.location.href = url;
                }
            });
        }
    }


    // ─────────────────────────────────────────────
    //  OFFERS / MILESTONES
    // ─────────────────────────────────────────────


    getOffers() {
        try {
            const script = this.querySelector('#ccd-offers-data');
            if (!script) return [];
            const parsed = JSON.parse(script.textContent);
            return parsed.map(offer => {
                if (offer.freebies && Array.isArray(offer.freebies)) {
                    const seen = new Set();
                    offer.freebies = offer.freebies.filter(f => {
                        if (!f.variantId || seen.has(String(f.variantId))) return false;
                        seen.add(String(f.variantId));
                        return true;
                    });
                    offer.allVariantIds = offer.freebies.map(f => String(f.variantId));
                    if (offer.variantId && !offer.allVariantIds.includes(String(offer.variantId))) {
                        offer.allVariantIds.push(String(offer.variantId));
                    }
                } else if (offer.variantId) {
                    offer.allVariantIds = [String(offer.variantId)];
                    offer.freebies = [{
                        productId: offer.productId,
                        variantId: offer.variantId,
                        available: true,
                        inventoryQuantity: null
                    }];
                } else {
                    offer.allVariantIds = [];
                    offer.freebies = [];
                }
                return offer;
            });
        } catch (e) {
            console.error('CCD: Error parsing offers', e);
            return [];
        }
    }


    renderMilestones() {
        if (!this.milestoneLayer || !this.offers.length) return;
        this.milestoneLayer.innerHTML = '';


        const count        = this.offers.length;
        const widthPerBlock = 100 / count;


        this.offers.forEach((offer, index) => {
            const percent = (index + 1) * widthPerBlock;
            const el      = document.createElement('div');
            el.className       = 'ccd-milestone';
            el.style.left      = `${percent}%`;
            el.dataset.threshold = offer.threshold;


            const moneyVal = 'Rs. ' + (offer.threshold / 100).toLocaleString();


            let iconHtml = '';
            if (offer.icon && !offer.icon.includes('no-image')) {
                iconHtml = `<img src="${offer.icon}" class="icon-default" style="width:100%; height:100%; object-fit:contain; border-radius:50%;" alt="Reward">`;
            } else {
                iconHtml = `<svg class="icon-default" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>`;
            }


            el.innerHTML = `
                <div class="ccd-milestone-label-top">${offer.rewardText}</div>
                <div class="ccd-milestone-icon">
                   ${iconHtml}
                   <svg class="icon-check" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" style="display:none;"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div class="ccd-milestone-label-bottom">${moneyVal}</div>
            `;
            this.milestoneLayer.appendChild(el);
        });
    }


    // ─────────────────────────────────────────────
    //  EVENT HANDLING
    // ─────────────────────────────────────────────


    handleClick(e) {
        if (e.target.closest('.ccd-close-btn') || e.target.closest('.ccd-overlay')) {
            this.close();
            return;
        }


        const removeBtn = e.target.closest('.ccd-remove-btn');
        if (removeBtn) {
            e.preventDefault();
            this.changeQty(parseInt(removeBtn.dataset.line, 10), 0, removeBtn.closest('.ccd-item'));
            return;
        }


        // ── Quantity +/- buttons ──────────────────
        // Delegated so it keeps working after fetchCart() replaces the
        // .ccd-items-wrapper innerHTML (inline onclick handlers do not
        // survive/would go stale after a re-render).
        const qtyBtn = e.target.closest('.ccd-qty-btn');
        if (qtyBtn) {
            e.preventDefault();
            if (this.qtyBusy) return; // ignore rapid double clicks mid-request

            const line    = parseInt(qtyBtn.dataset.line, 10);
            const row     = qtyBtn.closest('.ccd-item');
            const wrapper = qtyBtn.closest('.ccd-qty-wrapper');
            const input   = wrapper ? wrapper.querySelector('.ccd-qty-input') : null;
            let qty       = input ? parseInt(input.value, 10) : NaN;
            if (isNaN(qty)) qty = 1;

            qty = qtyBtn.dataset.qtyChange === 'plus' ? qty + 1 : qty - 1;
            if (qty < 0) qty = 0;

            this.changeQty(line, qty, row);
            return;
        }


        // ── Coupon "use" buttons ───────────────────
        // Moved here (from a second addEventListener in initCoupons)
        // so the listener is not re-registered every time initCoupons()
        // runs (which happens on every fetchCart() re-render).
        const couponBtn = e.target.closest('.ccd-use-coupon');
        if (couponBtn) {
            e.preventDefault();
            const code  = couponBtn.dataset.code;
            const input = this.querySelector('#ccd-coupon-input');
            if (input) input.value = code;
            this.applyCoupon(code);
        }
    }


    async handleGlobalSubmit(e) {
        if (e.target.action && e.target.action.includes('/cart/add')) {
            e.preventDefault();
            const submitBtn    = e.target.querySelector('[type="submit"]');
            const originalText = submitBtn ? submitBtn.innerText : '';
            if (submitBtn) { submitBtn.setAttribute('disabled', 'true'); submitBtn.innerText = 'Adding...'; }
            const formData = new FormData(e.target);
            const variantId = formData.get('id');
            const addQty = parseInt(formData.get('quantity') || 1, 10);
            const upsellCard = e.target.closest('.ccd-upsell-card');
            const upsellErrorEl = upsellCard ? upsellCard.querySelector('.ccd-upsell-stock-error') : null;

            try {
                // Ensure we have current cart state
                if (!this.lastCart || !this.lastCart.items) {
                    try {
                        const cartRes = await fetch(`/cart.js?_ts=${Date.now()}`);
                        this.lastCart = await cartRes.json();
                    } catch (cErr) {}
                }

                // Check if this variant is already in the cart (as a normal item, not freebie)
                if (this.lastCart && this.lastCart.items && variantId) {
                    const existingItemIndex = this.lastCart.items.findIndex(item => 
                        String(item.variant_id) === String(variantId) && 
                        (!item.properties || (!item.properties['_freebie'] && !item.properties['_milestone_freebie']))
                    );

                    if (existingItemIndex !== -1) {
                        // The item is ALREADY in the cart.
                        // Increasing quantity via /cart/change.js enforces Shopify's inventory limit
                        const line = existingItemIndex + 1;
                        const currentQty = this.lastCart.items[existingItemIndex].quantity;
                        const targetQty = currentQty + addQty;

                        console.log(`CCD: Item already in cart at line ${line} (current: ${currentQty}). Attempting to increase to ${targetQty}...`);

                        const rows = this.querySelectorAll('.ccd-item');
                        const row = rows[existingItemIndex];
                        const errorEl = row ? row.querySelector('.ccd-item-stock-error') : null;

                        const changeRes = await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ line: line, quantity: targetQty })
                        });
                        const changeData = await changeRes.json().catch(() => null);

                        if (!changeRes.ok) {
                            const message =
                                (changeData && (changeData.description || changeData.message)) ||
                                `Only ${currentQty} item(s) available in stock.`;

                            console.warn('CCD: Upsell add rejected due to inventory limit:', message);

                            // Show the stock error message on the cart item
                            if (errorEl) {
                                errorEl.textContent = message;
                                errorEl.style.display = 'block';
                                setTimeout(() => { errorEl.style.display = 'none'; }, 5000);
                            }

                            if (row) {
                                row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }

                            // Show error on upsell card
                            if (upsellErrorEl) {
                                upsellErrorEl.textContent = message;
                                upsellErrorEl.style.display = 'block';
                                setTimeout(() => { upsellErrorEl.style.display = 'none'; }, 5000);
                            }

                            if (submitBtn) {
                                submitBtn.innerText = 'Max in cart';
                                setTimeout(() => {
                                    if (submitBtn) {
                                        submitBtn.removeAttribute('disabled');
                                        submitBtn.innerText = originalText;
                                    }
                                }, 3000);
                            }
                            return;
                        }

                        // Successfully updated quantity
                        await this.fetchCart();
                        this.open();
                        return;
                    }
                }

                // If not already in cart, call /cart/add.js
                const res = await fetch('/cart/add.js', { method: 'POST', body: formData });
                const data = await res.json().catch(() => null);

                if (!res.ok) {
                    const message =
                        (data && (data.description || data.message)) ||
                        'This item is out of stock.';
                    console.warn('CCD: Add to cart rejected', message);

                    if (upsellErrorEl) {
                        upsellErrorEl.textContent = message;
                        upsellErrorEl.style.display = 'block';
                        setTimeout(() => { upsellErrorEl.style.display = 'none'; }, 5000);
                    }

                    if (submitBtn) {
                        submitBtn.innerText = 'Out of stock';
                        setTimeout(() => {
                            if (submitBtn) {
                                submitBtn.removeAttribute('disabled');
                                submitBtn.innerText = originalText;
                            }
                        }, 3000);
                    }
                    return;
                }

                await this.fetchCart();
                this.open();
            } catch (err) {
                console.error('CCD: Add to cart failed', err);
            } finally {
                if (submitBtn && submitBtn.innerText === 'Adding...') {
                    submitBtn.removeAttribute('disabled');
                    submitBtn.innerText = originalText;
                }
            }
        }
    }


    // ─────────────────────────────────────────────
    //  DRAWER OPEN / CLOSE
    // ─────────────────────────────────────────────


    open() {
        if (this.drawer) {
            this.classList.add(this.classes.active);
            document.body.style.overflow = 'hidden';
        } else {
            console.warn('CCD: Drawer not found, initializing...');
            this.init();
            if (this.querySelector('.ccd-drawer')) {
                this.classList.add(this.classes.active);
                document.body.style.overflow = 'hidden';
            }
        }
    }


    close() {
        this.classList.remove(this.classes.active);
        document.body.style.overflow = '';
    }


    // ─────────────────────────────────────────────
    //  CART FETCH & RE-RENDER
    // ─────────────────────────────────────────────


    async fetchCart() {
        try {
            const res  = await fetch(`/cart.js?_ts=${Date.now()}`);
            const cart = await res.json();
            this.lastCart = cart;


            // Keep the header's count badge ([data-nh-cart-count]) in sync immediately,
            // even if the section re-render below fails.
            this.syncHeaderCartCount(cart.item_count);


            try {
                // Fetch the updated cart drawer markup from the current page
                const pageUrl = `${window.location.pathname}?_ts=${Date.now()}`;
                const pageRes = await fetch(pageUrl);
                if (pageRes.ok) {
                    const pageText = await pageRes.text();
                    const doc      = new DOMParser().parseFromString(pageText, 'text/html');
                    const newItems = doc.querySelector('.ccd-items-wrapper');
                    if (newItems && this.itemsContainer) {
                        this.itemsContainer.innerHTML = newItems.innerHTML;
                    }


                    const newFooter     = doc.querySelector('.ccd-footer');
                    const currentFooter = this.querySelector('.ccd-footer');
                    if (newFooter && currentFooter) {
                        currentFooter.innerHTML = newFooter.innerHTML;
                        this.bindFooterEvents();
                    }


                    // Re-init dynamic features after DOM replacement
                    this.initCoupons();
                    this.initSpecialInstructions();
                }
            } catch (htmlErr) {
                console.warn('CCD: HTML fetch error', htmlErr);
            }


            this.updateProgress(cart);


            // Keep the theme's header cart icon (count bubble) in sync
            this.refreshHeaderCart();


        } catch (err) {
            console.error('CCD: FetchCart error', err);
        }
    }


    // ─────────────────────────────────────────────
    //  HEADER CART ICON SYNC
    //  Re-renders the theme's <cart-icon> from the server
    //  via the Section Rendering API so the count bubble
    //  updates without a full page refresh.
    // ─────────────────────────────────────────────


    async refreshHeaderCart() {
        try {
            const currentIcon = document.querySelector('cart-icon');
            if (!currentIcon) return;


            // The header section wrapper looks like <div id="shopify-section-...">
            const wrapper = currentIcon.closest('[id^="shopify-section-"]');
            if (!wrapper) return;
            const sectionId = wrapper.id.replace('shopify-section-', '');


            const res  = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
            const text = await res.text();
            const doc  = new DOMParser().parseFromString(text, 'text/html');


            const newIcon = doc.querySelector('cart-icon');
            if (newIcon) {
                currentIcon.innerHTML = newIcon.innerHTML;   // updates the count bubble
                currentIcon.className = newIcon.className;   // syncs the --has-cart class
            }
        } catch (e) {
            console.warn('CCD: header cart refresh failed', e);
        }
    }


    // ─────────────────────────────────────────────
    //  HEADER COUNT BADGE SYNC
    //  The theme header also renders its own count badge(s)
    //  marked with [data-nh-cart-count]. Update them directly
    //  so the number stays correct without a page reload.
    // ─────────────────────────────────────────────


    syncHeaderCartCount(count) {
        const value = Number(count) || 0;
        const badges = document.querySelectorAll('[data-nh-cart-count]');
        if (!badges.length) return;


        badges.forEach(badge => {
            badge.textContent = value;
            // Common pattern: hide the bubble when the cart is empty.
            badge.toggleAttribute('hidden', value === 0);
            badge.classList.toggle('is-empty', value === 0);
        });
    }


    // ─────────────────────────────────────────────
    //  QUANTITY CHANGE
    // ─────────────────────────────────────────────


    async changeQty(line, qty, rowEl) {
        line = parseInt(line, 10);
        qty  = parseInt(qty, 10);
        if (isNaN(line) || isNaN(qty)) return;


        const rows    = this.querySelectorAll('.ccd-item');
        const row     = rowEl || rows[line - 1];
        const errorEl = row ? row.querySelector('.ccd-item-stock-error') : null;
        const input   = row ? row.querySelector('.ccd-qty-input') : null;
        const prevQty = input ? input.value : null; // to restore visually if rejected


        if (row) row.style.opacity = '0.5';
        if (errorEl) { errorEl.style.display = 'none'; errorEl.textContent = ''; }


        if (qty === 0 && this.lastCart && this.lastCart.items[line - 1]) {
            const item = this.lastCart.items[line - 1];
            const isGift = (item.properties && (
                item.properties['_freebie'] === 'true' ||
                item.properties['_freebie'] === true ||
                item.properties['_milestone_freebie'] === 'true' ||
                item.properties['_FreeGift'] === 'true' ||
                item.properties['_FreeGift'] === true
            )) || (this.offers && this.offers.some(o => {
                if (o.variantId && String(o.variantId) === String(item.variant_id)) return true;
                if (o.allVariantIds && o.allVariantIds.includes(String(item.variant_id))) return true;
                return false;
            }));
            if (isGift) {
                const variantId = item.variant_id;
                this.removedVariantIds.add(variantId);
                this.removedVariantIds.add(String(variantId));
                if (this.offers) {
                    const matchingOffer = this.offers.find(o => 
                        (o.allVariantIds && o.allVariantIds.includes(String(variantId))) ||
                        (o.variantId && String(o.variantId) === String(variantId))
                    );
                    if (matchingOffer && matchingOffer.allVariantIds) {
                        matchingOffer.allVariantIds.forEach(vid => {
                            this.removedVariantIds.add(vid);
                            this.removedVariantIds.add(parseInt(vid, 10));
                        });
                    }
                }
                console.log('CCD: User removed free gift item', variantId);
            }
        }


        this.qtyBusy = true;
        try {
            const res = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ line: line, quantity: qty })
            });


            // Shopify's /cart/change.js returns a non-2xx status (usually 422)
            // when the requested quantity exceeds available inventory for a
            // tracked variant that does NOT allow overselling. Previously this
            // response was never checked, so the request silently failed and
            // the drawer just re-rendered as if nothing had happened.
            const data = await res.json().catch(() => null);

            if (!res.ok) {
                const message =
                    (data && (data.description || data.message)) ||
                    'This item just went out of stock at the quantity requested.';

                console.warn('CCD: Qty change rejected (stock)', message);

                if (errorEl) {
                    errorEl.textContent = message;
                    errorEl.style.display = 'block';
                    setTimeout(() => { errorEl.style.display = 'none'; }, 5000);
                }

                // Cart was NOT changed server-side, so just restore the UI
                // instead of doing a full fetchCart().
                if (input && prevQty !== null) input.value = prevQty;
                if (row) row.style.opacity = '1';
                return;
            }


            await this.fetchCart();
        } catch (e) {
            console.error('CCD: Qty change error', e);
            if (row) row.style.opacity = '1';
            if (errorEl) {
                errorEl.textContent = 'Something went wrong. Please try again.';
                errorEl.style.display = 'block';
                setTimeout(() => { errorEl.style.display = 'none'; }, 5000);
            }
        } finally {
            this.qtyBusy = false;
        }
    }


    // ─────────────────────────────────────────────
    //  PROGRESS BAR & MILESTONES
    // ─────────────────────────────────────────────


    getQualifyingPrice(cart) {
        if (!cart || !cart.items) return 0;
        let totalPrice = cart.total_price;
        cart.items.forEach(item => {
            const isGift = (item.properties && (
                item.properties['_freebie'] === 'true' ||
                item.properties['_freebie'] === true ||
                item.properties['_milestone_freebie'] === 'true' ||
                item.properties['_FreeGift'] === 'true' ||
                item.properties['_FreeGift'] === true ||
                item.properties['_FreeGiftDropdown'] === 'true'
            )) || (this.offers && this.offers.some(o => {
                if (o.variantId && String(o.variantId) === String(item.variant_id)) return true;
                if (o.allVariantIds && o.allVariantIds.includes(String(item.variant_id))) return true;
                return false;
            }));

            if (isGift) {
                totalPrice -= (item.final_line_price || item.line_price || (item.price * item.quantity));
            }
        });
        return Math.max(0, totalPrice);
    }


    updateProgress(cart) {
        if (!cart) return;
        const totalPrice = this.getQualifyingPrice(cart);


        if (!this.offers || this.offers.length === 0) return;
        const sortedOffers = this.offers;


        // Piecewise progress calculation
        let percentage   = 0;
        const segmentSize = 100 / sortedOffers.length;
        let prevThreshold = 0;


        for (let i = 0; i < sortedOffers.length; i++) {
            const currentThreshold = sortedOffers[i].threshold;


            if (totalPrice < currentThreshold) {
                const range           = currentThreshold - prevThreshold;
                const spentInSegment  = totalPrice - prevThreshold;
                let segmentProgress   = (spentInSegment > 0 ? spentInSegment : 0) / range;
                if (segmentProgress < 0) segmentProgress = 0;
                percentage = (i * segmentSize) + (segmentProgress * segmentSize);
                break;
            } else {
                percentage    = (i + 1) * segmentSize;
                prevThreshold = currentThreshold;
            }
        }


        if (totalPrice >= sortedOffers[sortedOffers.length - 1].threshold) percentage = 100;
        if (percentage > 100) percentage = 100;


        // Update fill bar
        if (this.progressBar) {
            this.progressBar.style.width = `${percentage}%`;
        } else {
            this.progressBar = this.querySelector('.ccd-progress-fill');
            if (this.progressBar) this.progressBar.style.width = `${percentage}%`;
        }


        // Update header item count
        const title = this.querySelector('.ccd-title');
        if (title) {
            title.innerHTML = `Your Cart (<span id="ccd-count">${cart.item_count || 0}</span> item${cart.item_count === 1 ? '' : 's'})`;
        }


        // Update milestone icons & check for confetti
        const milestones = this.querySelectorAll('.ccd-milestone');
        let currentUnlockedCount = 0;


        milestones.forEach(m => {
            const threshold  = parseInt(m.dataset.threshold, 10);
            const iconDefault = m.querySelector('.icon-default');
            const iconCheck   = m.querySelector('.icon-check');


            if (totalPrice >= threshold) {
                m.classList.add('reached');
                if (iconDefault) iconDefault.style.display = 'none';
                if (iconCheck)   iconCheck.style.display   = 'block';
                currentUnlockedCount++;
            } else {
                m.classList.remove('reached');
                if (iconDefault) iconDefault.style.display = 'block';
                if (iconCheck)   iconCheck.style.display   = 'none';
            }
        });


        if (this.firstLoad) {
            this.lastUnlockedCount = currentUnlockedCount;
            this.firstLoad = false;
        } else {
            if (currentUnlockedCount > this.lastUnlockedCount) this.fireConfetti();
            this.lastUnlockedCount = currentUnlockedCount;
        }


        // Status message
        let nextOffer = sortedOffers.find(offer => offer.threshold > totalPrice);
        let message   = '';


        if (nextOffer) {
            const amountRemaining = (nextOffer.threshold - totalPrice) / 100;
            const amountText      = amountRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 });
            message = `You are ₹${amountText} away from <strong>${nextOffer.rewardText}</strong>!`;
        } else {
            message = "🎉 You've unlocked all offers!";
        }


        if (this.progressText) this.progressText.innerHTML = message;


        // Auto add/remove free gifts
        this.manageFreeGifts(sortedOffers, cart);
    }


    // ─────────────────────────────────────────────
    //  FREE GIFT MANAGEMENT
    // ─────────────────────────────────────────────


    async manageFreeGifts(allOffers, cart) {
        if (!allOffers || !allOffers.length || !cart) return;
        if (this.isSyncingGifts) return;

        const qualifyingPrice = this.getQualifyingPrice(cart);
        const isGiftOffer = offer => (offer.type === 'product' || (offer.allVariantIds && offer.allVariantIds.length > 0) || Boolean(offer.variantId)) &&
                                     ((offer.allVariantIds && offer.allVariantIds.length > 0) || Boolean(offer.variantId));
        const giftOffers = allOffers.filter(isGiftOffer);
        if (!giftOffers.length) return;

        const unlockedOffers = giftOffers.filter(offer => offer.threshold <= qualifyingPrice);
        const lockedOffers   = giftOffers.filter(offer => offer.threshold > qualifyingPrice);

        this.isSyncingGifts = true;
        try {
            let cleanUpNeeded = false;

            // 1. Remove gifts that are no longer qualified
            for (const offer of lockedOffers) {
                const targetVariantIds = (offer.allVariantIds && offer.allVariantIds.length > 0)
                    ? offer.allVariantIds
                    : [String(offer.variantId)];

                // Clear any removed tracking so if customer re-qualifies later, gift can be added again
                targetVariantIds.forEach(vid => {
                    this.removedVariantIds.delete(vid);
                    this.removedVariantIds.delete(parseInt(vid, 10));
                });

                // Find any line items in cart matching this offer's variants
                const lineItemsToRemove = cart.items.filter(item => targetVariantIds.includes(String(item.variant_id)));

                for (const lineItem of lineItemsToRemove) {
                    console.log('CCD: Removing unqualified gift', offer.rewardText || lineItem.variant_id);
                    try {
                        const lineIndex = cart.items.indexOf(lineItem) + 1;
                        const res = await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ line: lineIndex, quantity: 0 })
                        });
                        if (res.ok) {
                            cleanUpNeeded = true;
                        }
                    } catch (e) {
                        console.error('CCD: Gift removal failed', e);
                    }
                }
            }

            if (cleanUpNeeded) {
                await this.fetchCart();
                return;
            }

            // 2. Add unlocked gifts
            for (const offer of unlockedOffers) {
                const targetVariantIds = (offer.allVariantIds && offer.allVariantIds.length > 0)
                    ? offer.allVariantIds
                    : [String(offer.variantId)];

                // If user deliberately removed this gift, don't re-add
                const isUserRemoved = targetVariantIds.some(vid => 
                    this.removedVariantIds.has(vid) || this.removedVariantIds.has(parseInt(vid, 10))
                );
                if (isUserRemoved) {
                    continue;
                }

                // Check if any freebie from this offer is already in the cart
                const alreadyInCart = cart.items.some(item => targetVariantIds.includes(String(item.variant_id)));
                if (alreadyInCart) {
                    continue; // Already has a freebie for this tier! Keep it, do not switch or add another!
                }

                // Filter pool: ONLY include freebies that are available and have stock quantity > 0 (if quantity is tracked)
                let pool = (offer.freebies || []).filter(item => {
                    if (!item || !item.variantId) return false;
                    if (!item.available) return false;
                    // Make sure freebie product which doesn't have qty is not added
                    if (typeof item.inventoryQuantity === 'number' && item.inventoryQuantity <= 0) return false;
                    return true;
                });

                if (!pool.length) {
                    console.warn('CCD: No available freebie with stock found for offer', offer.rewardText);
                    continue;
                }

                // Randomly select based on quantity
                let chosen = null;
                const hasQuantities = pool.some(item => typeof item.inventoryQuantity === 'number' && item.inventoryQuantity > 0);
                if (hasQuantities) {
                    // Weighted random selection by stock quantity
                    const totalQty = pool.reduce((sum, item) => sum + (Math.max(0, item.inventoryQuantity) || 1), 0);
                    let randomNum = Math.random() * totalQty;
                    for (const candidate of pool) {
                        const weight = (Math.max(0, candidate.inventoryQuantity) || 1);
                        if (randomNum <= weight) {
                            chosen = candidate;
                            break;
                        }
                        randomNum -= weight;
                    }
                    if (!chosen) chosen = pool[0];
                } else {
                    // Uniform random selection among available pool
                    chosen = pool[Math.floor(Math.random() * pool.length)];
                }

                if (!chosen || !chosen.variantId) continue;

                console.log('CCD: Auto-adding randomly chosen free gift', chosen.title || chosen.variantId);
                try {
                    const res = await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id: chosen.variantId,
                            quantity: 1,
                            properties: {
                                '_freebie': 'true',
                                '_milestone_freebie': 'true'
                            }
                        })
                    });
                    if (res.ok) {
                        await this.fetchCart();
                        return; // Stop after adding one, next cycle will handle others
                    } else {
                        console.warn('CCD: /cart/add.js rejected freebie (out of stock)', chosen.variantId);
                    }
                } catch (e) {
                    console.error('CCD: Auto Add Failed', e);
                }
            }
        } finally {
            this.isSyncingGifts = false;
        }
    }


    // ─────────────────────────────────────────────
    //  CONFETTI
    // ─────────────────────────────────────────────


    fireConfetti() {
        const container = document.createElement('div');
        container.className         = 'ccd-confetti-container';
        container.style.position    = 'absolute';
        container.style.top         = '0';
        container.style.left        = '0';
        container.style.width       = '100%';
        container.style.height      = '100%';
        container.style.pointerEvents = 'none';
        container.style.zIndex      = '999';
        container.style.overflow    = 'hidden';


        if (this.drawer) {
            this.drawer.appendChild(container);
        } else {
            this.appendChild(container);
        }


        for (let i = 0; i < 50; i++) {
            const p = document.createElement('div');
            p.className             = 'ccd-confetti-piece';
            p.style.left            = Math.random() * 100 + '%';
            p.style.animationDelay  = (Math.random() * 2) + 's';
            p.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`;
            p.style.width           = (Math.random() * 8 + 5) + 'px';
            p.style.height          = (Math.random() * 8 + 5) + 'px';
            container.appendChild(p);
        }


        setTimeout(() => { container.remove(); }, 5000);
    }


    // ─────────────────────────────────────────────
    //  COUPONS
    // ─────────────────────────────────────────────


    initCoupons() {
        const input         = this.querySelector('#ccd-coupon-input');
        const applyBtn      = this.querySelector('#ccd-apply-coupon');
        const message       = this.querySelector('#ccd-coupon-message');
        const toggleBtn     = this.querySelector('#ccd-toggle-coupons');
        const listContainer = this.querySelector('#ccd-coupon-list');
        const countLabel    = this.querySelector('#ccd-coupon-count');


        let coupons = [];
        try {
            const script = this.querySelector('#ccd-coupons-data');
            if (script) coupons = JSON.parse(script.textContent);
            console.log('CCD: Coupons loaded:', coupons);
        } catch (e) { console.error('CCD: Coupon Parse Error', e); }


        if (countLabel) countLabel.innerText = `${coupons.length} coupons available`;


        if (listContainer) {
            listContainer.innerHTML = '';
            coupons.forEach(c => {
                const row = document.createElement('div');
                row.style.cssText = 'display:flex; justify-content:space-between; margin-bottom:8px; align-items:center; border-bottom:1px solid #eee; padding-bottom:5px;';
                row.innerHTML = `
                    <div>
                       <div style="font-weight:700; color:#000;">${c.code}</div>
                       <div style="font-size:11px; color:#555;">${c.description}</div>
                    </div>
                    <button class="ccd-use-coupon" data-code="${c.code}" style="background:#000; color:#fff; border:none; border-radius:4px; padding:4px 8px; font-size:10px; cursor:pointer;">APPLY</button>
                `;
                listContainer.appendChild(row);
            });


            if (coupons.length === 0 && toggleBtn) {
                toggleBtn.style.display = 'none';
                if (countLabel) countLabel.innerText = '';
            }
        }


        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.preventDefault();
                listContainer.style.display = listContainer.style.display === 'none' ? 'block' : 'none';
            });
        }


        // NOTE: the ".ccd-use-coupon" click handling now lives in the
        // shared, delegated handleClick() so it isn't re-bound (and
        // stacked) every time initCoupons() runs on re-render.


        if (applyBtn) {
            applyBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const code = input.value.trim();
                if (code) this.applyCoupon(code);
            });
        }


        const activeCode = sessionStorage.getItem('ccd_discount_code');
        if (activeCode && input) {
            input.value = activeCode;
            this.showCouponSuccess(activeCode);
        }
    }


    applyCoupon(code) {
        sessionStorage.setItem('ccd_discount_code', code);
        this.showCouponSuccess(code);
    }


    showCouponSuccess(code) {
        const message = this.querySelector('#ccd-coupon-message');
        const input   = this.querySelector('#ccd-coupon-input');
        if (message) {
            message.style.display = 'block';
            message.style.color   = '#00b894';
            message.innerHTML     = `Discount code <strong>${code}</strong> applied! It will be calculated at checkout.`;
        }
        if (input) {
            input.disabled       = true;
            input.style.color    = '#00b894';
            input.style.fontWeight = '700';
        }
    }
}


customElements.define('custom-cart-drawer', CustomCartDrawer);